import { ReactNode, createContext, useCallback, useContext, useState } from "react";
import Modal from "./Modal";

export enum EModalId {
  DEFAULT,
  CONFIRM,
  RESERVATION_CANCEL,
  CHANGE_PASSWORD,
}

export enum EModalSize {
  FULL,
  LARGE,
  MEDIUM,
  SMALL,
  X_SMALL,
}

export interface IModal {
  id: EModalId;
  content: ReactNode;
  isOpen: boolean;
  title?: string;
  size: EModalSize;
  loading?: boolean;
  error?: string | null;
  disabled?: boolean;
  onModalClose?: () => void;
  noScroll?: boolean;
  overflow?: "auto" | "visible";
}

export const defaultModal: IModal = {
  id: EModalId.DEFAULT,
  content: null,
  isOpen: false,
  title: "",
  size: EModalSize.SMALL,
  loading: false,
  disabled: false,
  onModalClose: () => {},
  overflow: "auto",
};

export const ModalContext = createContext<{
  addModal: (modal: IModal) => void;
  closeModal: (id: EModalId, animate?: boolean) => void;
  updateModal: (id: number, modal: Partial<IModal>) => void;
}>({
  addModal: (modal: IModal) => {},
  closeModal: () => {},
  updateModal: () => {},
});

interface IProps {
  children: ReactNode;
}

export const useModalContext = () => useContext(ModalContext);

export const ModalContextProvider: React.FC<IProps> = ({ children }) => {
  const [modals, setModals] = useState<IModal[]>([]);

  const closeModal = useCallback((id: EModalId, animate: boolean = true) => {
    setModals((prevModals) => {
      if (animate) {
        return prevModals.map((modal) =>
          modal.id === id ? { ...modal, isOpen: false } : modal
        );
      } else {
        return prevModals.filter((modal) => modal.id !== id);
      }
    });
  }, []);

  const addModal = useCallback((modal: IModal) => {
    setModals((prevModals) => {
      const newModals = [...prevModals];
      const index = newModals.findIndex((m) => m.id === modal.id);
      if (index !== -1) {
        newModals[index] = modal;
      } else {
        newModals.push(modal);
      }
      return newModals;
    });
  }, []);

  const updateModal = useCallback((id: EModalId, modal: Partial<IModal>) => {
    setModals((prevModals) => {
      return prevModals.map((prevModal) =>
        prevModal.id === id ? { ...prevModal, ...modal } : prevModal
      );
    });
  }, []);

  return (
    <ModalContext.Provider value={{ addModal, closeModal, updateModal }}>
      {modals.map((modal) => (
        <Modal key={modal.id} modal={modal} />
      ))}
      {children}
    </ModalContext.Provider>
  );
};
